import React, { useMemo } from 'react';

/**
 * 💬 LivePreviewStrip — the "this room is talking right now" overlay.
 *
 * Ghost chat bubbles drifting down over the banner + emoji reactions rising
 * from the bottom. Items are derived deterministically from the space so they
 * stay stable across renders, and the animation pauses for offscreen /
 * non-focused cards.
 *
 * EXTRACTED from ExploreSpacesPage 08-15 (it lived inline beside LiveRailCard
 * since 07-xx) so the /explore swipe deck can show the same thing — Nina:
 * "we want more of a SHOW not tell philosophy… the ghost chatter in the
 * background". Two copies of a chat simulation drifting apart is exactly the
 * drift ArtStyle.js was collapsed to prevent, so there is one.
 *
 * ⚡ PERF SHAPE (PERF_TODO cost model): every animated property here is
 * `transform` or `opacity` — compositor work, effectively free — and the
 * element count is bounded at 5 bubbles + 5 reactions. There is ONE
 * `backdrop-filter` on the bubble pill, over a ≥0.78-opaque background; the
 * 08-13 audit's blur cuts targeted strips over MOVING content, which this is
 * not. Callers on constrained surfaces should still mount it on the focused
 * card only and pass `animate={false}` while dragging.
 *
 * `fallDistance` scales the drift to the container: the rail's strip is 195px,
 * a full-bleed swipe card is ~3x that, and a bubble that stops a third of the
 * way down reads as a glitch rather than as chatter.
 */

// Keyframes are OWNED here rather than by any page, so the component works
// anywhere it is mounted. Names are prefixed to avoid colliding with the
// (now removed) liveRail* copies.
const LPS_KEYFRAMES = `
@keyframes lpsBubbleFall {
  0%   { transform: translateY(-40px); opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateY(var(--lps-fall, 260px)); opacity: 0; }
}
@keyframes lpsReactionRise {
  0%   { transform: translateY(0) scale(0.7); opacity: 0; }
  18%  { opacity: 0.9; }
  100% { transform: translateY(var(--lps-rise, -200px)) scale(1.05); opacity: 0; }
}
`;

const LivePreviewStrip = ({
  space,
  accentColor = '#bf94ff',
  animate = true,
  fallDistance = 260,
  riseDistance = 200,
  bubbleScale = 1,
}) => {
  const items = useMemo(() => {
    const players = (space.players || []).filter(p => p.name);
    const emojiPool = (space.background_emojis && space.background_emojis.length > 0)
      ? space.background_emojis
      : (space.emojis && space.emojis.length > 0)
        ? space.emojis
        : ['✨', '💬', '🎉', '🔥', '💯', '😂', '👀'];

    const samplePlayers = players.length > 0
      ? players.slice(0, 4)
      : [{ name: 'guest' }];

    // Real ghost-chat messages beat fabricated name+emoji bubbles — the same
    // recent_messages the browse cards' GhostChat feed uses, here as drifting
    // stream-chat. Fabricated bubbles remain the fallback for quiet spaces
    // (measured 08-15: 10 of 50 live spaces carry recent_messages, so the
    // fallback is the common path and has to hold up on its own).
    const avatarByName = {};
    players.forEach(p => { if (p.profile_image_url) avatarByName[p.name] = p.profile_image_url; });
    const realMsgs = (space.recent_messages || []).filter(m => m && m.message).slice(-5);

    const bubbles = realMsgs.length > 0
      ? realMsgs.map((m, i) => ({
          key: `b-${space.id}-${i}`,
          name: m.sender || 'guest',
          avatarUrl: avatarByName[m.sender] || null,
          text: m.message.length > 48 ? m.message.slice(0, 48).trimEnd() + '…' : m.message,
          emoji: null,
          leftPct: i % 2 === 0 ? 5 : 30,
          duration: 14 + (i % 3) * 2,
          delay: i * 4.6,
        }))
      : [0, 1].map(i => {
          const p = samplePlayers[i % samplePlayers.length];
          return {
            key: `b-${space.id}-${i}`,
            name: p?.name || 'guest',
            avatarUrl: p?.profile_image_url || null,
            text: null,
            emoji: emojiPool[i % emojiPool.length],
            leftPct: 8 + i * 42,
            duration: 13 + i * 2.5,
            delay: i * 5.5,
          };
        });

    const reactions = Array.from({ length: 5 }).map((_, i) => ({
      key: `r-${space.id}-${i}`,
      emoji: emojiPool[(i + 1) % emojiPool.length],
      leftPct: 12 + i * 17 + (i % 2) * 6,
      duration: 5.5 + (i % 3) * 1.4,
      delay: (i * 1.1) % 4.5,
    }));

    return { bubbles, reactions };
  }, [space.id, space.players, space.recent_messages, space.background_emojis, space.emojis]);

  const playState = animate ? 'running' : 'paused';

  return (
    <div
      style={{
        ...styles.layer,
        '--lps-fall': `${fallDistance}px`,
        '--lps-rise': `-${riseDistance}px`,
      }}
      aria-hidden
    >
      <style>{LPS_KEYFRAMES}</style>
      <div style={styles.veil} />
      {items.reactions.map(r => (
        <div
          key={r.key}
          style={{
            ...styles.reaction,
            left: `${r.leftPct}%`,
            animation: `lpsReactionRise ${r.duration}s ease-out ${r.delay}s infinite`,
            animationPlayState: playState,
          }}
        >
          {r.emoji}
        </div>
      ))}
      {items.bubbles.map(b => (
        <div
          key={b.key}
          style={{
            ...styles.bubble,
            fontSize: `${0.78 * bubbleScale}rem`,
            left: `${b.leftPct}%`,
            maxWidth: '68%',
            animation: `lpsBubbleFall ${b.duration}s linear ${b.delay}s infinite`,
            animationPlayState: playState,
          }}
        >
          {b.avatarUrl ? (
            <img src={b.avatarUrl} alt="" style={styles.bubbleAvatar} />
          ) : (
            <div style={{ ...styles.bubbleAvatar, background: `${accentColor}66` }} />
          )}
          <span style={{ ...styles.bubbleText, minWidth: 0 }}>
            <span style={styles.bubbleName}>@{b.name}</span>
            {b.text ? (
              <span style={{ marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.text}</span>
            ) : (
              <span style={styles.bubbleEmoji}>{b.emoji}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

const styles = {
  layer: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 1,
  },
  veil: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(14,14,16,0.20) 0%, rgba(14,14,16,0.50) 100%)',
  },
  bubble: {
    position: 'absolute',
    left: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    padding: '4px 10px 4px 4px',
    backgroundColor: 'rgba(20,20,24,0.78)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    borderRadius: '999px',
    color: '#efeff1',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
    willChange: 'transform, opacity',
  },
  bubbleAvatar: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    backgroundColor: '#26262c',
  },
  bubbleText: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: '5px',
    lineHeight: 1.2,
  },
  bubbleName: {
    color: '#bf94ff',
    fontWeight: 600,
    // The pill caps at maxWidth 68%, but without these a long handle
    // ("@Interstellar Interviewer") pushed straight through it — only the
    // MESSAGE half was ever clamped. Measured on the swipe deck 08-15.
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  bubbleEmoji: {
    fontSize: '0.92rem',
  },
  reaction: {
    position: 'absolute',
    bottom: '-12px',
    fontSize: '1.2rem',
    lineHeight: 1,
    willChange: 'transform, opacity',
    userSelect: 'none',
  },
};

export default LivePreviewStrip;
